import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { AuthError, type SignUpInput } from "aws-amplify/auth";

import { amplifyAuthQueryKeys } from "~/queries/amplify/auth";
import { meQueryKeys } from "~/queries/identity/me";
import type { AuthProvider } from "~/types/Auth";

export type UnifiedAuth = ReturnType<typeof _useAuth>;

export function useAuthErrorQuery() {
  return useRouteQuery("error_description");
}

function _useAuth() {
  const { $amplify } = useNuxtApp();

  const {
    data: isAmplifyAuthenticated,
    isLoading: isAmplifyAuthenticatedLoading,
  } = useQuery({
    ...amplifyAuthQueryKeys.isAuthenticated(),
    enabled: () => !!$amplify,
    staleTime: Infinity, // we can keep it until it's manually invalidated
  });

  const isLoggedIn = computed(() => {
    if (isAmplifyAuthenticatedLoading.value) return false;

    return isAmplifyAuthenticated.value ?? false;
  });

  const nuxtApp = useNuxtApp();

  const getToken = async () => {
    const session = await $amplify?.auth.fetchAuthSession();

    const token = session?.tokens?.accessToken.toString();

    return token ? `Bearer ${token}` : undefined;
  };

  type LoginParams =
    | {
        strategy: "email";
        options: {
          email: string;
          password: string;
        };
      }
    | {
        strategy: "sso";
        options: {
          provider: AuthProvider;
          loginHint?: string;
        };
      }
    | {
        strategy: "passwordless";
        options: {
          username: string;
        };
      };

  const { broadcastAmplifyAuthStateChange } = useAmplifyAuthBroadcastChannel();
  const queryClient = useQueryClient();

  const onSuccessfulAmplifyLogin = async () => {
    queryClient.setQueryData(
      amplifyAuthQueryKeys.isAuthenticated().queryKey,
      true,
    );

    broadcastAmplifyAuthStateChange({ isLoggedIn: true });

    await queryClient.invalidateQueries(meQueryKeys.detail());
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const loginWith = async ({ options, strategy }: LoginParams) => {
    if (strategy === "email") {
      let segmentUser;
      try {
        segmentUser = await nuxtApp.$segment.browser.value.user();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Error fetching segment user", e);
      }

      const analyticsContext = getAnalyticsHeaders(nuxtApp);
      const response = await $amplify?.auth.signIn({
        username: options.email,
        password: options.password,
        options: {
          authFlowType: "USER_PASSWORD_AUTH",
          clientMetadata: {
            trackingContext: JSON.stringify(analyticsContext),
            "Anonymous-Id": segmentUser?.anonymousId() ?? "",
          },
        },
      });

      if (!response?.isSignedIn) {
        if (response?.nextStep.signInStep === "RESET_PASSWORD") {
          throw new AuthError({
            name: "PasswordResetRequiredException",
            message: "Reset password required",
          });
        }

        throw new Error();
      }

      await onSuccessfulAmplifyLogin();
    } else if (strategy === "sso") {
      if (!isLoggedIn.value)
        await $amplify?.auth.signInWithRedirect({
          provider: options.provider,
          options: {
            loginHint: options.loginHint,
          },
        });
    } else {
      if (!getIsHostLiverpool()) {
        // eslint-disable-next-line no-console
        console.warn(
          "Passwordless login is only supported on the Liverpool platform",
        );

        return;
      }

      const initAuthResponse = await $amplify?.auth.signIn({
        username: options.username,
        options: {
          authFlowType: "CUSTOM_WITHOUT_SRP",
        },
      });

      if (
        initAuthResponse?.nextStep.signInStep !==
        "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
      ) {
        throw new Error("Unexpected next step for auth initialisation");
      }

      const confirmResponse = await $amplify?.auth.confirmSignIn({
        challengeResponse: document.cookie,
      });

      if (!confirmResponse?.isSignedIn) {
        throw new Error("Failed to confirm sign in");
      }

      await onSuccessfulAmplifyLogin();
    }
  };

  type RegisterParams = {
    email: string;
    password: string;
    userAttributes?: Required<SignUpInput>["options"]["userAttributes"];
  };

  const register = async (params: RegisterParams) => {
    let segmentUser;
    try {
      segmentUser = await nuxtApp.$segment.browser.value.user();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error fetching segment user", e);
    }

    const username = params.email.toLowerCase();
    const analyticsContext = getAnalyticsHeaders(nuxtApp);
    analyticsContext.properties.anonymousId = segmentUser?.anonymousId() ?? "";

    await $amplify?.auth.signUp({
      username,
      password: params.password,
      options: {
        userAttributes: { email: params.email, ...params.userAttributes },
        autoSignIn: true,
        clientMetadata: {
          trackingContext: JSON.stringify(analyticsContext),
        },
      },
    });

    if (getIsHostLiverpool()) {
      await loginWith({
        strategy: "passwordless",
        options: {
          username,
        },
      });
    } else {
      await loginWith({
        strategy: "email",
        options: {
          email: username,
          password: params.password,
        },
      });
    }
  };

  const refreshTokens = async () => {
    $amplify?.auth.fetchAuthSession({
      forceRefresh: true,
    });
  };

  const logout = async () => {
    await $amplify?.auth.signOut();

    if (getIsHostLiverpool()) {
      await logoutFromLiverpool();
    }

    await queryClient.refetchQueries({
      queryKey: amplifyAuthQueryKeys.isAuthenticated().queryKey,
    });

    broadcastAmplifyAuthStateChange({ isLoggedIn: false });
  };

  const redirectCookie = useCookie("amplify-auth-redirect");

  return {
    isLoggedIn,

    getToken,
    loginWith,
    refreshTokens,
    logout,
    register,

    redirectCookie,
  };
}

export const useAuth = createSharedComposable(_useAuth);
